// View model of the public Track & Trace page. Built on the server from an order (see
// server/functions/orders/trackTracePage.ts) and rendered by src/trackTrace. The page is only served to a
// link with the postal code of the address; `details` holds what is personal.

// From this date (YYYY-MM-DD) every reseller uses the new page and the switch in the settings is gone. Set it at the
// release to two weeks after the release date; until it is set resellers switch the new page on themselves.
export const NEW_PAGE_FOR_ALL_FROM: string | null = null

/** Whether the switchover date has passed, so every reseller uses the new page. */
export function newPageForAll(now: Date = new Date(), from: string | null = NEW_PAGE_FOR_ALL_FROM): boolean {
    if (!from) {
        return false
    }

    const [year, month, day] = from.split('-').map(Number)
    return now >= new Date(year, month - 1, day)
}

export const trackTraceLanguages = ['nl', 'en', 'de', 'fr'] as const

export type TrackTraceLanguage = typeof trackTraceLanguages[number]

export type TrackTraceState =
    | 'expected' // Announced, date not yet known
    | 'scheduled' // Date known, not on the road yet
    | 'onTheWay' // Out for pickup/delivery
    | 'completed' // Delivered or collected
    | 'notCompleted' // Attempt failed, see message
    | 'returning' // Going back to the sender
    | 'onHold' // Kept at the depot
    | 'cancelled'

// Keys of the reseller's status messages in settings.orders.trackTrace
export const trackTraceMessageKeys = [
    'pickupNotCompletedMessage',
    'pickupNotHomeMessage',
    'pickupCancelledMessage',
    'deliveryNotCompletedRefusedMessage',
    'deliveryNotCompletedRetourMessage',
    'deliveryNotCompletedNoFollowUpMessage',
    'deliveryNotCompletedRescheduleMessage',
    'deliveryNotHomeRetourMessage',
    'deliveryNotHomeNoFollowUpMessage',
    'deliveryNotHomeRescheduleMessage',
    'deliveryNotHomeOnHoldMessage',
    'deliveryMancoMessage',
    'deliveryCancelledMessage'
] as const

export type TrackTraceMessageKey = typeof trackTraceMessageKeys[number]

export type TrackTraceEventType = 'created' | 'atDepot' | 'planned' | 'onTheWay' | 'rescheduled' | 'completed' | 'notCompleted' | 'onHold' | 'returned' | 'cancelled' | 'manco'

// Why an attempt failed: the collo reasons of the courier app (dbCodes.colloReasons) and a refused delivery
export const trackTraceReasons = ['nothome', 'notready', 'invalid', 'addressnotfound', 'wrongaddress', 'companyaddressclosed', 'verificationfailed', 'delayed', 'overmacht', 'other', 'refused'] as const

export type TrackTraceReason = typeof trackTraceReasons[number]

export interface TrackTraceEvent {
    type: TrackTraceEventType
    timestamp: string
    // Only for a failed attempt; the note is the reason specification the reseller set up
    reason?: TrackTraceReason
    reasonNote?: string
}

export type TrackTraceColloState = 'expected' | 'atDepot' | 'onTheWay' | 'completed' | 'notCompleted' | 'onHold' | 'returned' | 'cancelled' | 'missing'

export interface TrackTraceBranding {
    resellerName: string
    logoUrl: string | null
    color: string
    outForDeliveryIcon: 'bike' | 'cargo-bike' | 'car'
    phone: string
    email: string
    whatsapp: string
    footerTitle: string
    footerMessage: string
    companyAddress: {name: string, street: string, nr: string, addition: string, postalCode: string, city: string} | null
    faq: {question: string, answer: string}[]
    languages: TrackTraceLanguage[]
    defaultLanguage: TrackTraceLanguage
    // Null when the reseller switched the photo off; a missing photo for a screen size is null
    photo: {desktop: string | null, mobile: string | null} | null
    message: string | null
}

export const RECIPIENT_MESSAGE_MAX = 80

// Smallest photos the reseller can upload, so they stay sharp on large screens. The welcome mails have a photo of
// their own, at the size of the mobile photo of the page.
export const trackTracePhotoSizes = {
    desktop: {width: 2400, height: 400},
    mobile: {width: 1100, height: 600},
    welcomeCustomer: {width: 1100, height: 600},
    welcomeUser: {width: 1100, height: 600}
} as const

export type TrackTracePhotoKind = keyof typeof trackTracePhotoSizes

// Where each photo is saved, under the reseller settings
export const trackTracePhotoPaths: Record<TrackTracePhotoKind, string> = {
    desktop: 'orders.trackTrace.photoDesktop',
    mobile: 'orders.trackTrace.photoMobile',
    welcomeCustomer: 'customers.welcomeEmailPhoto',
    welcomeUser: 'users.welcomeEmailPhoto'
}

// none; recipient: only the own address; recipientCourier: also the courier on the day of delivery;
// recipientCourierStops: also the number of stops before this one
export const trackTraceMapOptions = ['none', 'recipient', 'recipientCourier', 'recipientCourierStops'] as const

export type TrackTraceMapOption = typeof trackTraceMapOptions[number]

// Older positions of the courier are not shown
export const COURIER_POSITION_MAX_AGE_MINUTES = 15

export interface TrackTraceMap {
    recipient: {lat: number, lng: number}
    // Only on the day of delivery, from the start of the route until the stop is done, with a recent position
    courier: {lat: number, lng: number, updatedAt: string} | null
    // Stops of the route before this one that are not done yet; never where they are
    stopsBefore: number | null
    eta: string | null // HH:mm
}

export interface TrackTraceView {
    trackTrace: string
    addressIndex: number
    addressType: 'pickup' | 'delivery'
    state: TrackTraceState
    // 0 announced, 1 planned, 2 on the way, 3 completed
    step: 0 | 1 | 2 | 3
    date: string | null // YYYY-MM-DD
    window: {from: string, to: string} | null // HH:mm
    arrivedAt: string | null // HH:mm
    message: {key: TrackTraceMessageKey, text: string} | null // text is the reseller's message in the requested language, may be empty
    outcome: {neighbourNr: string | null, mailbox: boolean, safePlace: string | null} | null
    requirements: {
        signature: boolean
        statedAddressOnly: boolean
        mailbox: boolean
        minimumAge: number | null
        idCheck: boolean
        verificationCode: boolean
    }
    city: string
    senderName: string
    colli: {state: TrackTraceColloState}[]
    timeline: TrackTraceEvent[]
    map: TrackTraceMap | null
    details: TrackTraceDetails | null
    branding: TrackTraceBranding
}

export interface TrackTraceDetails {
    address: {
        name: string
        attention: string
        street: string
        nr: string
        addition: string
        street2: string
        postalCode: string
        city: string
        country: string
    }
    reference: string
    colli: {barcode: string, type: string}[]
    proof: {signee: string, signature: string | null, images: string[]} | null
    // Null when the reseller offers no actions
    actions: TrackTraceActions | null
}

export const defaultSafePlaceOptions = ['Achterdeur', 'Schuur of berging', 'Garage', 'Overkapping']

// The option for a place that is not in the reseller's list, the recipient describes it in the note
export const OTHER_SAFE_PLACE = 'Anders'

// closed: the stop is completed, cancelled or returning (a pickup point also once the route is ready); notAtDepot: the
// parcel is not at the depot yet; notPossible: not for this shipment (e.g. several delivery addresses or a partner).
// An action the options of the address rule out is not offered at all.
export type TrackTraceActionBlock = 'closed' | 'notAtDepot' | 'notPossible'

export const DEFAULT_PICKUP_PERIOD = 5

// Delivery days a recipient chooses from for another delivery day
export const DEFAULT_DELIVERY_DAY_COUNT = 5

// The option a link from a mail opens on the page (?actie=), per action of the recipient
export const TRACK_TRACE_ACTION_PARAMS = {
    safePlace: 'veiligePlek',
    neighbour: 'buren',
    pickupPoint: 'ophalen',
    instructions: 'instructie',
    deliveryDay: 'andereDag'
} as const

export type TrackTraceActionKey = keyof typeof TRACK_TRACE_ACTION_PARAMS

export interface TrackTracePickupPoint {
    id: string
    name: string
    address: string
    openingHours: string
}

export type TrackTraceChoice =
    | {type: 'safePlace', place: string, note: string}
    | {type: 'neighbour', nr: string}
    | {type: 'pickupPoint', pickupPoint: TrackTracePickupPoint, until: string}
    | {type: 'deliveryDay', date: string}

export interface TrackTraceAction {
    available: boolean
    blockedBy: TrackTraceActionBlock | null
}

export interface TrackTraceActions {
    // Null when the reseller switched the action off
    safePlace: TrackTraceAction | null
    neighbour: TrackTraceAction | null
    pickupPoint: TrackTraceAction | null
    instructions: TrackTraceAction | null
    deliveryDay: TrackTraceAction | null
    safePlaceOptions: string[]
    pickupPoints: TrackTracePickupPoint[]
    // YYYY-MM-DD, the last day to collect when the recipient chooses a pickup point today
    pickupUntil: string
    // YYYY-MM-DD, the days the recipient can choose for another delivery day
    deliveryDays: string[]
    choice: TrackTraceChoice | null
    instructionsText: string
}

export type TrackTraceActionRequest =
    | {type: 'safePlace', place: string, note?: string, accepted: boolean}
    | {type: 'neighbour', nr: string}
    | {type: 'pickupPoint', id: string}
    | {type: 'deliveryDay', date: string}
    | {type: 'instructions', text: string}
    | {type: 'clearChoice'}
